import { useEffect, useState } from 'react';
import api, { formatPrice } from '../api';
import { useToast } from '../hooks/use-toast';
import { Trash2, Plus, X } from 'lucide-react';

const emptyForm = { name: '', category: 'bags', price: 0, color: 'Cognac', image: '', images: '', description: '', details: '', isNew: false, stock: 100 };

export default function Admin() {
  const { toast } = useToast();
  const [authed, setAuthed] = useState(!!sessionStorage.getItem('bb_admin_pw'));
  const [password, setPassword] = useState('');
  const [tab, setTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [editing, setEditing] = useState(null); // product or null
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    try {
      const [p, o, c, n, r] = await Promise.all([
        api.get('/products'), api.get('/orders'), api.get('/contact'), api.get('/newsletter'), api.get('/reviews'),
      ]);
      setProducts(p.data); setOrders(o.data); setContacts(c.data); setNewsletter(n.data); setReviews(r.data);
    } catch { setAuthed(false); sessionStorage.removeItem('bb_admin_pw'); }
  };

  useEffect(() => { if (authed) load(); }, [authed]);

  const doLogin = async (e) => {
    e.preventDefault();
    sessionStorage.setItem('bb_admin_pw', password);
    try {
      await api.post('/admin/verify');
      setAuthed(true);
      toast({ title: 'Welcome, admin' });
    } catch {
      sessionStorage.removeItem('bb_admin_pw');
      toast({ title: 'Wrong password' });
    }
  };

  const openNew = () => { setEditing({}); setForm(emptyForm); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p, images: (p.images || []).join('\n'), details: (p.details || []).join('\n') }); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form,
      price: Number(form.price), stock: Number(form.stock),
      images: form.images.split('\n').map(s => s.trim()).filter(Boolean),
      details: form.details.split('\n').map(s => s.trim()).filter(Boolean),
    };
    try {
      if (editing?.id) await api.put(`/products/${editing.id}`, payload);
      else await api.post('/products', payload);
      setEditing(null); load();
      toast({ title: 'Saved' });
    } catch (err) { toast({ title: 'Save failed', description: err.response?.data?.detail || '' }); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    await api.delete(`/products/${id}`); load();
  };

  const setStatus = async (oid, status) => {
    await api.patch(`/orders/${oid}/status`, { status }); load();
  };

  const logout = () => { sessionStorage.removeItem('bb_admin_pw'); setAuthed(false); };

  if (!authed) {
    return (
      <main className="max-w-md mx-auto px-6 py-24">
        <p className="text-[11px] tracking-widest-plus uppercase text-[#C6410E] mb-4">Admin</p>
        <h1 className="font-serif-display text-5xl mb-8">Studio access.</h1>
        <form onSubmit={doLogin} className="space-y-6">
          <div>
            <label className="text-[11px] tracking-widest-plus uppercase block mb-2">Password</label>
            <input type="password" required value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-transparent border-b border-black/30 focus:border-[#C6410E] outline-none py-2 text-sm" />
          </div>
          <button className="btn-primary w-full">Enter</button>
        </form>
      </main>
    );
  }

  return (
    <main className="max-w-7xl mx-auto px-6 py-12">
      <div className="flex justify-between items-center mb-10">
        <h1 className="font-serif-display text-4xl">Studio</h1>
        <button onClick={logout} className="text-[11px] tracking-widest-plus uppercase link-underline">Sign out</button>
      </div>

      <div className="flex gap-6 border-b border-black/10 mb-10">
        {['products', 'orders', 'reviews', 'contacts', 'newsletter'].map(t => (
          <button key={t} onClick={() => setTab(t)} className={`text-[11px] tracking-widest-plus uppercase pb-3 -mb-px border-b-2 ${tab === t ? 'border-[#C6410E] text-[#C6410E]' : 'border-transparent'}`}>{t}</button>
        ))}
      </div>

      {tab === 'products' && (
        <div>
          <div className="flex justify-between mb-6">
            <p className="text-sm text-[#1a1a1a]/60">{products.length} products</p>
            <button onClick={openNew} className="btn-primary inline-flex items-center gap-2"><Plus className="w-4 h-4" /> New product</button>
          </div>
          <table className="w-full text-sm">
            <thead className="text-[11px] tracking-widest-plus uppercase text-[#1a1a1a]/60 border-b border-black/10">
              <tr><th className="text-left py-3">Product</th><th className="text-left">Category</th><th className="text-left">Price</th><th className="text-left">Stock</th><th></th></tr>
            </thead>
            <tbody>
              {products.map(p => (
                <tr key={p.id} className="border-b border-black/5 hover:bg-[#E9E1D4]/40">
                  <td className="py-3 flex items-center gap-3"><img src={p.image} alt="" className="w-10 h-10 object-cover" /> {p.name}</td>
                  <td>{p.category}</td>
                  <td>{formatPrice(p.price)}</td>
                  <td>{p.stock}</td>
                  <td className="text-right">
                    <button onClick={() => openEdit(p)} className="text-[11px] tracking-widest-plus uppercase link-underline mr-4">Edit</button>
                    <button onClick={() => del(p.id)} className="text-red-700"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'orders' && (
        <div className="space-y-4">
          {orders.length === 0 && <p className="text-sm text-[#1a1a1a]/60">No orders yet.</p>}
          {orders.map(o => (
            <div key={o.id} className="border border-black/10 p-6">
              <div className="flex justify-between mb-3">
                <div>
                  <p className="font-serif-display text-xl">{o.id}</p>
                  <p className="text-xs text-[#1a1a1a]/60">{new Date(o.created_at).toLocaleString()}</p>
                </div>
                <select value={o.status} onChange={e => setStatus(o.id, e.target.value)} className="bg-transparent border-b border-black/30 text-sm outline-none">
                  {['pending_payment', 'placed', 'shipped', 'delivered', 'cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <p className="text-sm">{o.shipping.name} · {o.shipping.phone} · {o.shipping.email}</p>
              <p className="text-sm text-[#1a1a1a]/70">{o.shipping.address}, {o.shipping.city} — {o.shipping.pincode}</p>
              <ul className="text-sm mt-3 space-y-1">
                {o.items.map((it, i) => <li key={i}>{it.name} × {it.qty} — {formatPrice(it.price * it.qty)}</li>)}
              </ul>
              <p className="text-sm mt-3 font-medium">Total {formatPrice(o.total)} · {o.payment_method === 'razorpay' ? 'Razorpay' : 'COD'}
                {o.payment_method === 'razorpay' && <span data-testid={`admin-payment-status-${o.id}`} className={`ml-2 text-[10px] tracking-widest-plus uppercase px-2 py-0.5 ${o.payment_status === 'paid' ? 'bg-green-700 text-white' : o.payment_status === 'failed' ? 'bg-red-700 text-white' : 'bg-[#E9E1D4]'}`}>{o.payment_status}</span>}
                {o.razorpay_payment_id && <span className="ml-2 text-xs text-[#1a1a1a]/50">{o.razorpay_payment_id}</span>}
              </p>
            </div>
          ))}
        </div>
      )}

      {tab === 'contacts' && (
        <div className="space-y-4">
          {contacts.length === 0 && <p className="text-sm text-[#1a1a1a]/60">No messages yet.</p>}
          {contacts.map(c => (
            <div key={c.id} className="border border-black/10 p-6">
              <div className="flex justify-between mb-2"><p className="font-medium">{c.name} · {c.email}</p><p className="text-xs text-[#1a1a1a]/50">{new Date(c.created_at).toLocaleString()}</p></div>
              <p className="text-[11px] tracking-widest-plus uppercase text-[#C6410E] mb-2">{c.subject}</p>
              <p className="text-sm text-[#1a1a1a]/80 whitespace-pre-wrap">{c.message}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'reviews' && (
        <div className="space-y-4">
          {reviews.length === 0 && <p className="text-sm text-[#1a1a1a]/60">No reviews yet.</p>}
          {reviews.map(r => (
            <div key={r.id} data-testid={`admin-review-${r.id}`} className="border border-black/10 p-6">
              <div className="flex justify-between mb-2 gap-4">
                <p className="font-medium">{products.find(p => p.id === r.product_id)?.name || r.product_id} · {'★'.repeat(r.rating)}<span className="text-[#1a1a1a]/30">{'★'.repeat(5 - r.rating)}</span></p>
                <button data-testid={`admin-review-delete-${r.id}`} onClick={async () => { if (window.confirm('Delete this review?')) { await api.delete(`/reviews/${r.id}`); load(); } }} className="text-[#1a1a1a]/50 hover:text-[#C6410E]"><Trash2 className="w-4 h-4" /></button>
              </div>
              <p className="font-serif-display text-lg">{r.title}</p>
              <p className="text-sm text-[#1a1a1a]/80 mt-1">{r.body}</p>
              <p className="text-xs text-[#1a1a1a]/50 mt-2">{r.author} · {new Date(r.created_at).toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'newsletter' && (
        <div>
          <p className="text-sm text-[#1a1a1a]/60 mb-4">{newsletter.length} subscribers</p>
          <ul className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
            {newsletter.map(n => <li key={n.email} className="border border-black/10 px-3 py-2">{n.email}</li>)}
          </ul>
        </div>
      )}

      {editing !== null && (
        <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-[#F4EEE4] max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 relative">
            <button type="button" onClick={() => setEditing(null)} className="absolute top-4 right-4"><X className="w-5 h-5" /></button>
            <h2 className="font-serif-display text-3xl mb-6">{editing?.id ? 'Edit product' : 'New product'}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {[['name','Name'],['category','Category'],['price','Price'],['color','Colour'],['stock','Stock'],['image','Main image URL']].map(([k, l]) => (
                <div key={k} className={k === 'image' ? 'md:col-span-2' : ''}>
                  <label className="text-[11px] tracking-widest-plus uppercase block mb-1">{l}</label>
                  <input required={['name','category','price','image'].includes(k)} value={form[k]} onChange={e => setForm({...form, [k]: e.target.value})} className="w-full bg-transparent border-b border-black/30 py-2 text-sm outline-none focus:border-[#C6410E]" />
                </div>
              ))}
              <div className="md:col-span-2">
                <label className="text-[11px] tracking-widest-plus uppercase block mb-1">Extra images (one URL per line)</label>
                <textarea rows={3} value={form.images} onChange={e => setForm({...form, images: e.target.value})} className="w-full bg-transparent border border-black/20 p-2 text-sm outline-none focus:border-[#C6410E]" />
              </div>
              <div className="md:col-span-2">
                <label className="text-[11px] tracking-widest-plus uppercase block mb-1">Description</label>
                <textarea rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="w-full bg-transparent border border-black/20 p-2 text-sm outline-none focus:border-[#C6410E]" />
              </div>
              <div className="md:col-span-2">
                <label className="text-[11px] tracking-widest-plus uppercase block mb-1">Details (one per line)</label>
                <textarea rows={4} value={form.details} onChange={e => setForm({...form, details: e.target.value})} className="w-full bg-transparent border border-black/20 p-2 text-sm outline-none focus:border-[#C6410E]" />
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.isNew} onChange={e => setForm({...form, isNew: e.target.checked})} /> Mark as new</label>
            </div>
            <div className="mt-8 flex gap-4">
              <button className="btn-primary">Save</button>
              <button type="button" onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </main>
  );
}
